package com.zi.support.shared

/**
 * Incident severity 1 (normal) to 5 (most severe), worked out from what the reporting app sent so old
 * incidents get a level too. Shared by web, Android and iOS for the incident badge and filter.
 *
 * - 3: anything to do with completing a booking (Driver Hub "Completion rejected", "Checksum mismatch",
 *   "Upload too large", or any failure on /api/complete_booking), even when the cause was the network.
 * - 4: app crashes.
 * - 1: network trouble (no signal, unable to resolve host, timeouts, refused connections).
 * - 2: everything else.
 * A record whose own "severity" field is already 1-5 keeps that level.
 */
object IncidentSeverity {
    const val NORMAL = 1
    const val COMPLETION = 3
    const val CRASH = 4
    private const val OTHER = 2

    val levels = (5 downTo 1).toList()

    private val completion = listOf(
        "complete_booking", "completebooking", "complete booking", "completion", "checksum mismatch",
        "upload too large", "pipeline",
    )
    private val crash = listOf("crash", "fatal")
    private val network = listOf(
        "network failure", "unable to resolve host", "unknownhost", "no address associated", "timed out", "timeout",
        "failed to connect", "connection reset", "connection refused", "connection abort", "device was offline",
        "socketexception", "sslhandshake", "network is unreachable", "err_internet_disconnected",
    )

    fun level(errorType: String, message: String, severity: String = ""): Int {
        severity.trim().toIntOrNull()?.takeIf { it in 1..5 }?.let { return it }
        val type = errorType.lowercase()
        val text = "$type $message".lowercase()
        return when {
            completion.any { it in text } -> COMPLETION
            crash.any { it in type } -> CRASH
            network.any { it in text } -> NORMAL
            else -> OTHER
        }
    }

    fun label(level: Int): String = when (level) {
        1 -> "Normal"
        2 -> "Low"
        3 -> "Medium"
        4 -> "High"
        else -> "Critical"
    }

    /** The severity colour as 0xRRGGBB, the same on every platform (web: the --sev values in styles.css). */
    fun color(level: Int): Long = when (level) {
        1 -> 0x64748B
        2 -> 0x2E6BF6
        3 -> 0xD97706
        4 -> 0xEA580C
        else -> 0xDC2626
    }

    /** "Severity 3 · Medium" */
    fun title(level: Int): String = "Severity $level · ${label(level)}"
}
